import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "./../components/ui/button";
import { Input } from "./../components/ui/input";
import { useNavigate, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./../components/ui/card";
import { Badge } from "./../components/ui/badge";
import { Avatar, AvatarFallback } from "./../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./../components/ui/select";
import { useAuth } from "../context/AuthContext";
import Loader from "./../components/ui/Loader";
import { getFullImageUrl } from "../utils/imageHandler";
import {
  MapPin,
  Search,
  Code,
  Palette,
  BarChart3,
  Headphones,
  Shield,
  Globe,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  DollarSign,
  Clock,
  Filter,
  Sparkles,
} from "lucide-react";

import api from "./../api/client";
import { applierGetJobs } from "./../api/jobs";
import SEOHead from "./../components/SEO/SEOHead";
import { toast } from "react-hot-toast";
import { getJobPath, isJobExpired } from "../utils/jobSlug";
import { getJobCardSnippet } from "../utils/jobFormat";
import { glassCard, glassCardHover, glassPanel, glassHeroSearch } from "../utils/glassStyles";

const JOB_FILTERS = [
  { id: "all", label: "All jobs" },
  { id: "active", label: "Active" },
  { id: "recent", label: "Last 24 hours" },
  { id: "week", label: "This week" },
  { id: "expired", label: "Expired" },
];

const buildFilterParams = (filterId) => {
  switch (filterId) {
    case "active":
      return { activeOnly: true };
    case "recent":
      return { days: 1 };
    case "week":
      return { days: 7 };
    case "expired":
      return { expiredOnly: true };
    default:
      return {};
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

function AnimatedSection({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function HeroJobAnimation() {
  const floats = [
    { Icon: Briefcase, x: "8%", y: "18%", delay: 0, size: "w-10 h-10" },
    { Icon: Briefcase, x: "88%", y: "22%", delay: 1.2, size: "w-8 h-8" },
    { Icon: MapPin, x: "92%", y: "55%", delay: 0.6, size: "w-9 h-9" },
    { Icon: DollarSign, x: "5%", y: "62%", delay: 1.8, size: "w-8 h-8" },
    { Icon: Briefcase, x: "78%", y: "78%", delay: 2.4, size: "w-7 h-7" },
    { Icon: MapPin, x: "15%", y: "82%", delay: 0.9, size: "w-7 h-7" },
  ];

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden hidden md:block">
      {floats.map(({ Icon, x, y, delay, size }, i) => (
        <motion.div
          key={i}
          className={`absolute ${size} rounded-2xl bg-primary/10 border border-primary/20 backdrop-blur-sm flex items-center justify-center text-primary/40 dark:text-accent/40`}
          style={{ left: x, top: y }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: [0.25, 0.55, 0.25],
            y: [0, -14, 0],
            rotate: [0, 6, -6, 0],
          }}
          transition={{
            duration: 5 + i * 0.5,
            delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <Icon className="w-1/2 h-1/2" />
        </motion.div>
      ))}
    </div>
  );
}

function JobFilterBar({ value, onChange, disabled, total }) {
  return (
    <div className={`rounded-2xl ${glassPanel} py-3 px-3 sm:py-4 sm:px-5`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-sm font-semibold flex items-center gap-2 shrink-0">
          <Filter className="w-4 h-4 text-primary" />
          <span className="hidden sm:inline">Filter jobs</span>
          <span className="sm:hidden">Filters</span>
        </p>
        {total != null && (
          <p className="text-xs text-muted-foreground shrink-0">{total} result{total !== 1 ? "s" : ""}</p>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none snap-x snap-mandatory">
        {JOB_FILTERS.map((f) => (
          <Button
            key={f.id}
            size="sm"
            variant={value === f.id ? "default" : "outline"}
            disabled={disabled}
            onClick={() => onChange(f.id)}
            className={`rounded-full shrink-0 snap-start h-9 px-3.5 text-xs sm:text-sm backdrop-blur-sm ${
              value !== f.id
                ? "bg-white/10 dark:bg-secondary/80 border-white/20 dark:border-border hover:bg-white/20 dark:hover:bg-secondary"
                : "shadow-md shadow-primary/20"
            }`}
          >
            {f.id === "recent" && <Clock className="w-3.5 h-3.5 mr-1" />}
            {f.id === "active" && <Sparkles className="w-3.5 h-3.5 mr-1" />}
            {f.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function KeywordSearchInput({ value, onChange, onSelect, onSubmit, className = "" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const query = value.trim();

    if (query.length < 2) {
      setSuggestions([]);
      setOpen(false);
      setLoadingSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const res = await applierGetJobs({ q: query, limit: 6, page: 1 });
        const items = (res.data.jobs || []).map((job) => ({
          id: job.id || job.jobId,
          title: job.title,
          company: job.companyName || job.recruiter?.companyName || "Unknown Company",
          job,
        }));
        setSuggestions(items);
        setOpen(items.length > 0);
        setActiveIndex(-1);
      } catch {
        setSuggestions([]);
        setOpen(false);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 280);

    return () => clearTimeout(debounceRef.current);
  }, [value]);

  const pickSuggestion = (item) => {
    onChange(item.title);
    setOpen(false);
    setActiveIndex(-1);
    onSelect?.(item);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "Enter") onSubmit?.();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) pickSuggestion(suggestions[activeIndex]);
      else onSubmit?.();
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          placeholder="Keywords — job title, skills..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          className={`h-11 sm:h-12 rounded-xl bg-background/50 dark:bg-white/[0.06] border-white/30 dark:border-white/10 backdrop-blur-sm pr-9 ${className}`}
          autoComplete="off"
        />
        {loadingSuggestions && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1.5 w-full rounded-xl border border-border/60 bg-popover/95 backdrop-blur-xl shadow-xl overflow-hidden">
          <ul className="py-1 max-h-56 overflow-y-auto">
            {suggestions.map((item, idx) => (
              <li key={item.id || idx}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickSuggestion(item)}
                  className={`w-full text-left px-3 py-2.5 transition-colors ${
                    idx === activeIndex ? "bg-primary/10" : "hover:bg-muted/60"
                  }`}
                >
                  <p className="text-sm font-medium text-foreground line-clamp-1">{item.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{item.company}</p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function JobGrid({ jobs, onJobClick, compact = false }) {
  return (
    <div className={`grid gap-3 ${compact ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}>
      {jobs.map((job, index) => {
        const snippet = getJobCardSnippet(job, 85);
        return (
        <motion.div
          key={job.id || job.jobId}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.35), ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -3 }}
          className="min-w-0"
        >
        <Card
          className={`group cursor-pointer rounded-xl sm:rounded-2xl h-full overflow-hidden relative ${glassCardHover}`}
          onClick={() => onJobClick(job)}
        >
          {isJobExpired(job) && (
            <Badge
              variant="secondary"
              className="absolute top-2 right-2 z-10 text-[10px] px-2 py-0.5 h-5 bg-amber-500/90 text-white border-0 shadow-sm font-semibold"
            >
              Expired
            </Badge>
          )}
          {job.badge && !isJobExpired(job) && (
            <Badge
              variant={job.badge === "Urgent" ? "destructive" : "secondary"}
              className="absolute top-2 right-2 z-10 text-[10px] px-2 py-0.5 h-5 shadow-sm"
            >
              {job.badge}
            </Badge>
          )}
          <CardHeader className="p-3.5 sm:p-5 pb-2 sm:pb-3 pr-16">
            <div className="flex items-start gap-2.5 sm:gap-3">
              <Avatar className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl border bg-white/70 dark:bg-white/10 shadow-sm overflow-hidden shrink-0">
                {(job.companyLogo || job.recruiter?.logoUrl) && (
                  <img
                    src={getFullImageUrl(job.companyLogo || job.recruiter?.logoUrl)}
                    alt={job.companyName}
                    className="w-full h-full object-contain p-0.5 sm:p-1"
                  />
                )}
                <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold text-xs uppercase">
                  {job.companyName?.[0] || job.recruiter?.companyName?.[0] || "J"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="text-[10px] font-semibold text-primary uppercase tracking-wide truncate mb-0.5">
                  {job.company?.name || job.companyName}
                </p>
                <CardTitle className="text-sm sm:text-base line-clamp-2 leading-snug font-semibold">
                  {job.title}
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3.5 sm:p-5 pt-0 space-y-2">
            {snippet && (
              <p className="text-[11px] sm:text-xs text-muted-foreground/90 line-clamp-2 leading-relaxed">
                {snippet}
              </p>
            )}
            <div className="flex items-center gap-3 text-[11px] sm:text-xs text-muted-foreground">
              <span className="flex items-center gap-1 min-w-0 flex-1">
                <MapPin className="w-3.5 h-3.5 shrink-0 text-primary/70" />
                <span className="truncate">{job.location}</span>
              </span>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 shrink-0 font-normal">
                {job.type?.replace("_", " ") || "Full-time"}
              </Badge>
            </div>
            {!compact && (job.salaryMin || job.salaryMax) && (
              <p className="text-xs sm:text-sm font-semibold text-primary flex items-center gap-1">
                <DollarSign className="w-3.5 h-3.5" />
                {job.salaryMin && job.salaryMax
                  ? `${job.salaryMin.toLocaleString()} – ${job.salaryMax.toLocaleString()} ETB`
                  : "Competitive"}
              </p>
            )}
            <Button
              asChild
              variant="secondary"
              size="sm"
              className="w-full h-9 sm:h-10 group-hover:bg-primary group-hover:text-primary-foreground transition-colors rounded-lg sm:rounded-xl text-xs sm:text-sm mt-1"
            >
              <Link to={getJobPath(job)} onClick={(e) => e.stopPropagation()}>
                View Details
              </Link>
            </Button>
          </CardContent>
        </Card>
        </motion.div>
        );
      })}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const [jobFilter, setJobFilter] = useState("all");
  const [searching, setSearching] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const JOBS_PER_PAGE = 12;
  const { user } = useAuth();

  const fetchJobs = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      const res = await applierGetJobs(filters);
      const jobsData = res.data.jobs.map((job) => ({
        ...job,
        company: {
          name: job.companyName || job.recruiter?.companyName || "Unknown Company",
        },
      }));
      setJobs(jobsData);
      setPagination(res.data.pagination);
      setError("");
    } catch (err) {
      console.error(err);
      setJobs([]);
      setError("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  const buildFilters = useCallback(
    (page = 1, overrides = {}) => {
      const filterId = overrides.jobFilter ?? jobFilter;
      return {
        q: (overrides.keyword ?? keyword) || undefined,
        location: (overrides.location ?? location) || undefined,
        category: (overrides.category ?? category) || undefined,
        page,
        limit: JOBS_PER_PAGE,
        ...buildFilterParams(filterId),
      };
    },
    [keyword, location, category, jobFilter]
  );

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [catsRes] = await Promise.all([api.get("/jobs/categories")]);
        setCategories(catsRes.data || getFallbackCategories());
        await fetchJobs({ page: 1, limit: JOBS_PER_PAGE });
      } catch (err) {
        console.error("Error fetching categories:", err.response?.data || err.message);
        setCategories(getFallbackCategories());
        setError("Failed to load categories. Please try again.");
      }
    };
    fetchInitial();
  }, [fetchJobs]);

  const handleSearch = async (page = 1, overrides = {}) => {
    setSearching(true);
    try {
      if (overrides.keyword !== undefined) setKeyword(overrides.keyword);
      if (overrides.location !== undefined) setLocation(overrides.location);
      if (overrides.category !== undefined) setCategory(overrides.category);
      if (overrides.jobFilter !== undefined) setJobFilter(overrides.jobFilter);
      await fetchJobs(buildFilters(page, overrides));
    } catch (err) {
      console.error(err);
      setError("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleFilterChange = (filterId) => {
    handleSearch(1, { jobFilter: filterId });
  };

  const goToPage = (page) => {
    if (page < 1 || page > pagination.pages || page === pagination.page) return;
    handleSearch(page);
  };

  const getFallbackCategories = () => [
    { icon: Code, title: "Development", count: 245, color: "blue" },
    { icon: Palette, title: "Design", count: 189, color: "purple" },
    { icon: BarChart3, title: "Marketing", count: 132, color: "green" },
    { icon: Headphones, title: "Customer Service", count: 98, color: "orange" },
    { icon: Shield, title: "Security", count: 67, color: "red" },
    { icon: Globe, title: "Sales", count: 154, color: "blue" },
  ];

  const hasActiveSearch = !!(keyword || category || location);
  const hasJobFilter = jobFilter !== "all";
  const showJobResults = hasActiveSearch || hasJobFilter;
  const sectionTitle = hasActiveSearch
    ? "Search Results"
    : hasJobFilter
      ? JOB_FILTERS.find((f) => f.id === jobFilter)?.label + " Jobs"
      : "Featured Jobs";

  const handleJobClick = (job) => {
    const path = getJobPath(job);
    if (path !== "/") {
      navigate(path);
    } else {
      toast.error("Could not open job details");
    }
  };

  const paginationBar = pagination.pages > 1 && (
    <div className="flex justify-center items-center gap-2 mt-8">
      <Button variant="outline" size="icon" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page === 1 || searching}>
        <ChevronLeft className="w-5 h-5" />
      </Button>
      {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
        const pageNum = pagination.page <= 3 ? i + 1 : pagination.page - 2 + i;
        if (pageNum > pagination.pages) return null;
        return (
          <Button key={pageNum} variant={pagination.page === pageNum ? "default" : "outline"} size="sm" onClick={() => goToPage(pageNum)}>
            {pageNum}
          </Button>
        );
      })}
      <Button variant="outline" size="icon" onClick={() => goToPage(pagination.page + 1)} disabled={pagination.page === pagination.pages || searching}>
        <ChevronRight className="w-5 h-5" />
      </Button>
    </div>
  );

  return (
    <div className="relative min-h-screen bg-transparent">
      <SEOHead
        title="Shebeka — Find Your Next Job in Ethiopia | Top Job Portal"
        description="Shebeka connects Ethiopian job seekers with top employers. Search by location, category, and salary. Apply directly and track every application. Free for job seekers."
        keywords="jobs in Ethiopia, Ethiopian jobs, careers Ethiopia, employment Ethiopia, job search, hiring, recruitment, Addis Ababa jobs, software developer jobs, engineering jobs"
        url="/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Shebeka",
          url: "https://shebeka.com",
          description: "Ethiopia's leading job portal connecting job seekers with top employers",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://shebeka.com/jobs?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />

      {/* HERO SECTION */}
      <section className={`relative overflow-hidden px-3 sm:px-6 lg:px-8 xl:px-10 ${showJobResults ? "py-6 md:py-10" : "py-8 md:py-12 lg:py-14 xl:py-16"}`}>
        {/* Decorative background + job icons */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] xl:w-[1100px] h-[500px] xl:h-[600px] bg-gradient-to-b from-primary/15 via-accent/10 to-transparent dark:from-accent/8 dark:via-primary/5 dark:to-transparent rounded-full blur-3xl animate-pulse-soft" />
          <div className="absolute top-32 -left-20 w-72 h-72 bg-primary/10 dark:bg-primary/5 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 -right-16 w-64 h-64 bg-accent/10 dark:bg-accent/5 rounded-full blur-3xl animate-float [animation-delay:2s]" />
          <div
            className="absolute inset-0 opacity-[0.35] dark:opacity-[0.12]"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground) / 0.06) 1px, transparent 0)`,
              backgroundSize: "40px 40px",
            }}
          />
          <HeroJobAnimation />
        </div>

        <div className="max-w-[1440px] mx-auto">
          <div className={`${showJobResults ? "" : "lg:grid lg:grid-cols-2 lg:gap-10 xl:gap-16 lg:items-center"}`}>
            {/* Hero copy — left on large screens */}
            <div className={`text-center lg:text-left mb-6 sm:mb-8 lg:mb-0 max-w-3xl mx-auto lg:mx-0 lg:max-w-xl xl:max-w-2xl`}>
              <motion.h1
                custom={0}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                className="font-display font-bold tracking-tight text-foreground leading-[1.08] mb-3 sm:mb-4"
              >
                <span className="block text-[1.75rem] min-[400px]:text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl">
                  Find Your Next Opportunity
                </span>
                <span className="block text-[2rem] min-[400px]:text-[2.25rem] sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl mt-0.5">
                  with{" "}
                  <span className="text-brand-gradient bg-[length:200%_auto] animate-gradient-shift">
                    Shebeka
                  </span>
                </span>
              </motion.h1>

              <motion.p
                custom={1}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                className="text-sm sm:text-base md:text-lg xl:text-xl text-muted-foreground/90 max-w-md sm:max-w-lg lg:max-w-none mx-auto lg:mx-0 leading-relaxed px-1 font-sans"
              >
                Discover trusted opportunities from employers who share your values. Search, apply, and track your career with Shebeka.
              </motion.p>
            </div>

            {/* Search + filters — right on large screens */}
            <div className="w-full min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="w-full"
          >
            <Card className={`w-full rounded-2xl sm:rounded-3xl shadow-2xl shadow-primary/5 dark:shadow-black/20 ${glassHeroSearch}`}>
              <CardContent className="p-4 sm:p-6 lg:p-7 xl:p-8">
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  <KeywordSearchInput
                    value={keyword}
                    onChange={setKeyword}
                    onSubmit={() => handleSearch(1)}
                    onSelect={(item) => handleSearch(1, { keyword: item.title })}
                  />
                  <Input
                    id="hero-location"
                    placeholder="City or region"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    className="h-11 sm:h-12 rounded-xl bg-background/50 dark:bg-white/[0.06] border-white/30 dark:border-white/10 backdrop-blur-sm"
                  />
                  <Select
                    value={category || "all"}
                    onValueChange={(v) => handleSearch(1, { category: v === "all" ? "" : v })}
                  >
                    <SelectTrigger
                      id="hero-category"
                      className="h-11 sm:h-12 w-full rounded-xl bg-background/50 dark:bg-white/[0.06] border-white/30 dark:border-white/10 backdrop-blur-sm text-left [&>span]:truncate"
                    >
                      <SelectValue placeholder="All categories" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="all">All categories</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c.title} value={c.title}>
                          {c.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="lg"
                    onClick={() => handleSearch(1)}
                    disabled={searching}
                    className="h-11 sm:h-12 w-full rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
                  >
                    {searching ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Search className="w-5 h-5 mr-2" />}
                    Search Jobs on Shebeka
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Job filters */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="w-full mt-6"
          >
            <JobFilterBar
              value={jobFilter}
              onChange={handleFilterChange}
              disabled={loading || searching}
              total={pagination.total}
            />
          </motion.div>
            </div>
          </div>

          {/* Job results — below search when searching or filtering */}
          {showJobResults && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-[1440px] mx-auto mt-8"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{sectionTitle}</h2>
                {error && <p className="text-destructive text-sm">{error}</p>}
              </div>
              {loading ? (
                <Loader variant="jobs" count={12} label="Finding jobs for you..." />
              ) : jobs.length === 0 ? (
                <Card className={`py-12 ${glassCard}`}>
                  <CardContent className="text-center">
                    <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No jobs found. Try adjusting your search or filters.</p>
                    {hasJobFilter && (
                      <Button variant="outline" className="mt-4" onClick={() => handleFilterChange("all")}>
                        Clear filter
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <>
                  <JobGrid jobs={jobs} onJobClick={handleJobClick} compact />
                  {paginationBar}
                </>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* CATEGORIES */}
      {!showJobResults && (
        <section className="pt-4 pb-8 sm:pt-6 sm:pb-10 px-3 sm:px-6 lg:px-8 xl:px-10">
          <div className="max-w-[1440px] mx-auto">
            <AnimatedSection className="text-center mb-4 sm:mb-6">
              <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-bold tracking-tight mb-1.5">
                Browse by Category
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto">
                Tap a category to filter jobs instantly
              </p>
            </AnimatedSection>
            <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              {categories.slice(0, 6).map((cat, idx) => (
                <motion.div
                  key={cat.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: idx * 0.05 }}
                  whileHover={{ y: -2 }}
                >
                <Card
                  onClick={() => handleSearch(1, { category: cat.title })}
                  className={`cursor-pointer rounded-xl h-full ${glassCardHover}`}
                >
                  <CardContent className="flex flex-col items-center p-2.5 sm:p-4 text-center">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10 mb-1.5 sm:mb-2">
                      {cat.icon ? <cat.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" /> : <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />}
                    </div>
                    <p className="font-semibold text-[11px] sm:text-xs leading-tight line-clamp-2">{cat.title}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{cat.count}</p>
                  </CardContent>
                </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured jobs — default landing view only */}
      {!showJobResults && (
        <section className="pt-2 pb-10 sm:pt-4 sm:pb-12 px-3 sm:px-6 lg:px-8 xl:px-10">
          <div className="max-w-[1440px] mx-auto">
            <AnimatedSection className="mb-4 sm:mb-6">
              <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-bold tracking-tight">{sectionTitle}</h2>
              {error && <p className="text-destructive text-sm mt-1">{error}</p>}
            </AnimatedSection>
            {loading ? (
              <Loader variant="jobs" count={12} label="Loading featured jobs..." />
            ) : jobs.length === 0 ? (
              <Card className={`py-16 ${glassCard}`}>
                <CardContent className="text-center">
                  <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg text-muted-foreground">No jobs match this filter.</p>
                  {hasJobFilter && (
                    <Button variant="outline" className="mt-4" onClick={() => handleFilterChange("all")}>
                      Show all jobs
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <>
                <JobGrid jobs={jobs} onJobClick={handleJobClick} />
                {paginationBar}
              </>
            )}
          </div>
        </section>
      )}

      {!user && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <Card className={`max-w-4xl mx-auto rounded-2xl sm:rounded-3xl overflow-hidden border-primary/20 dark:border-border/70 ${glassCard}`}>
              <div className="h-1 bg-gradient-to-r from-primary via-primary/80 to-accent bg-[length:200%_auto] animate-gradient-shift" />
              <CardContent className="text-center py-10 sm:py-14 px-5 sm:px-6">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="w-14 h-14 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/30"
                >
                  <Sparkles className="w-7 h-7 text-white" />
                </motion.div>
                <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4">
                  Ready to Take the Next Step?
                </h3>
                <p className="text-lg text-muted-foreground mb-3 max-w-2xl mx-auto leading-relaxed">
                  Join thousands of professionals who found their next role with Shebeka.
                </p>
                <p className="text-sm text-muted-foreground mb-8 max-w-lg mx-auto">
                  Create your profile once, apply to multiple roles, and track every application from your dashboard.
                </p>
                <Button asChild size="lg" className="px-8 rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <Link to="/register">
                    Get Started Now
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </AnimatedSection>
        </section>
      )}
    </div>
  );
}
